<template>
  <section class="c-section c-section__suggest">
    <div class="l-block">
      <div class="l-block__header">
        <TitleSub icon="sitesearchrank" level="h2">
          <span class="title">関連ワード分析</span>
          <span class="description">
            自社サイトの関連するキーワードを分析する
          </span>
        </TitleSub>
      </div>
      <div class="inbox-area">
        <div class="control-area">
          <div class="textBlock">
            <span class="bold">
              ｢
              <span class="textRed">
                {{ keyword }}
              </span>
              ｣
            </span>
            と関連するキーワードと自社サイトの順位データを表示します。
          </div>
        </div>
      </div>
    </div>
    <div class="p-competing-actions">
      <ul class="c-graph-legend">
        <li class="c-graph-legend__item c-graph-legend__item--rank">
          1位
        </li>
        <li class="c-graph-legend__item c-graph-legend__item--rank">
          2位～5位
        </li>
        <li class="c-graph-legend__item c-graph-legend__item--rank">
          6位～10位
        </li>
        <li class="c-graph-legend__item c-graph-legend__item--rank">
          11位以下
        </li>
        <li class="c-graph-legend__item c-graph-legend__item--rank">
          圏外
        </li>
        <li class="c-graph-legend__item  c-graph-legend__item--hasTask">
          TODO・進行中にタスク登録済み
        </li>
        <li class="c-graph-legend__item  c-graph-legend__item--completedTask">
          タスク完了済み
        </li>
      </ul>
      <ButtonDownloadSuggestAnalysis
        v-if="isDownloadableCsv"
        :keyword="keyword"
        :limit="params.filter.limit"
        :offset="params.filter.offset"
        :project="project"
      />
    </div>
    <div class="p-competing-suggest">
      <ListSuggestAnalysis
        :items="data.items"
        :project="project"
        :selected="selected"
        :isLoading="isLoading"
        :version="version"
        :analysesStatus="data.analysesStatus"
        :tasks="data.onGoingTasks"
        :completedTasks="data.completedTasks"
        :duplicateCheckParams="duplicateCheckParams"
        :keywordId="keywordId"
        :startDate="startDate"
        @select="selectItem"
        ref="listSuggestAnalysis"
        @updateTab="updateTab"
        @duplicateCheck="duplicateCheck"
      />
      <ListSuggestSerps
        :project="project"
        :selected="selected"
        :startDate="startDate"
        :selectedTab="selectedTab"
      />
    </div>
    <Proposals
      v-if="!isLoading"
      :project="project"
      :proposal="proposal"
      :proposals="data.proposals"
      ref="proposal"
      @focus="focusProposal"
      @remove="removeProposal"
    />
  </section>
</template>

<script>
import ButtonDownloadSuggestAnalysis from '@/components/competitiveKeyword/ButtonDownloadSuggestAnalysis';
import ListSuggestAnalysis from '@/components/competitiveKeyword/ListSuggestAnalysis';
import ListSuggestSerps from '@/components/competitiveKeyword/ListSuggestSerps';
import Proposals from '@/components/proposal/Proposals';
import TitleSub from '@/components/TitleSub';
import apiProjects from '@/api/projects';
import utils from '@/utils';
import axios from 'axios';

export default {
  props: ['keyword', 'keywordId', 'project', 'startDate'],

  components: {
    ButtonDownloadSuggestAnalysis,
    ListSuggestAnalysis,
    ListSuggestSerps,
    Proposals,
    TitleSub
  },

  data() {
    return {
      data: {
        analysesStatus: 0,
        items: [],
        proposals: [],
        onGoingTasks: [],
        completedTasks: []
      },
      errorResponse: '',
      params: {
        filter: {
          limit: 1000,
          offset: 0
        },
        proposals: {
          limit: 1000
        }
      },
      duplicateCheckParams: {
        keyword: '',
        isWaiting: false,
        hasError: false
      },
      request: {
        cancelTokenProposal: null
      },
      selected: {},
      isLoading: true,
      isProcessing: false,
      version: 0,
      selectedTab: 'priority'
    };
  },

  computed: {
    isDownloadableCsv() {
      return this.data.analysesStatus === 2;
    },
    proposal() {
      if (this.data.proposals.length) {
        return this.data.proposals[0];
      }
      return null;
    }
  },

  created() {
    if (!this.keyword || !this.startDate) {
      this.$router.push({
        name: 'competitiveKeyword'
      });
    }
    this.getRelatedWordAnalysis();
  },

  destroyed() {
    if (this.request.cancelTokenProposal) {
      this.request.cancelTokenProposal.cancel();
    }
  },

  methods: {
    updateTab(value) {
      this.selectedTab = value;
    },
    getRelatedWordAnalysis() {
      this.isLoading = true;

      const params = {
        keyword: this.keyword,
        limit: this.params.filter.limit,
        offset: this.params.filter.offset
      };
      apiProjects.relatedWordAnalysis
        .version(this.auth.idToken, this.project.id, this.keywordId)
        .then(versionResponse => {
          this.version = versionResponse.data.version;

          apiProjects.relatedWordAnalysis
            .get(params, this.auth.idToken, this.project.id)
            .then(response => {
              this.data.analysesStatus = response.data.analysesStatus;
              this.data.items = response.data.items.map(item => {
                item.duplicatePagesCount = null;
                return item;
              });
              this.isLoading = false;

              this.getTasks();
              // キーワードリスト取得が完了してからAI自動提案リストを取得（タイミングによってAI自動改善提案の取得が先に完了してしまうため
              this.getProposal();
            })
            .catch(error => {
              if (error.response) {
                this.errorResponse = error.response.data.errors[0].userMessage;
              }
              this.isLoading = false;
            });
        })
        .catch(error => {
          this.isLoading = false;
        });
    },

    getProposal() {
      const params = {
        type: 'rewrite',
        // 分析日で取得するように修正
        date: this.$options.filters.dateText(utils.getDateResetHours()),
        limit: this.params.proposals.limit,
        discoverKeywordId: this.$route.params.keywordId
      };

      this.request.cancelTokenProposal = axios.CancelToken.source();

      apiProjects.proposals
        .get(
          params,
          this.auth.idToken,
          this.project.id,
          this.request.cancelTokenProposal.token
        )
        .then(response => {
          let proposals = this.data.proposals.slice();
          proposals.push(
            ...response.data.items
              .filter(item => {
                // 記事対策キーワードの分析リリース後は記事対策キーワードのみAI提案を出す
                if (this.version == 2) {
                  let priorityKeywords = this.data.items
                    .filter(item => item.isPriority)
                    .map(item => item.keyword);
                  return priorityKeywords.includes(item.keyword);
                }
                return true;
              })
              .map(item => {
                item.type = 'rewrite'; // rewrite固定なのでハードコーディング
                return item;
              })
          );
          this.data.proposals = proposals;
        })
        .catch(error => {});
    },

    getTasks() {
      apiProjects.tasks
        .get(
          {
            columns: 'todo,doing,done',
            order: 'createdDate:asc'
          },
          this.auth.idToken,
          this.project.id
        )
        .then(response => {
          this.data.onGoingTasks = response.data.items.filter(
            item =>
              (item.column === 'todo' || item.column === 'doing') &&
              item.keyword
          );
          this.data.completedTasks = response.data.items.filter(
            item => item.column === 'done' && item.keyword
          );
        })
        .catch(error => {});
    },

    checkResult() {
      this.isProcessing = true;

      const params = {
        keywordId: this.keywordId,
        limit: 1000,
        offset: 0
      };

      apiProjects.competitorKeywords
        .get(params, this.auth.idToken, this.project.id)
        .then(response => {
          if (
            response.data.keywords.length &&
            response.data.keywords[0].items.some(item => {
              if (!item.isOwnSite) {
                return true;
              }
            })
          ) {
            this.goResult();
          } else {
            this.goInput();
          }
        })
        .catch(error => {
          this.goInput();
        });
    },

    selectItem(item) {
      this.selected = item;
    },

    focusProposal() {
      this.$refs.listSuggestAnalysis.scrollToKeyword(this.proposal.keyword);
    },
    removeProposal(proposal) {
      const index = this.data.proposals.findIndex(
        item => item.id === proposal.id
      );
      this.data.proposals.splice(index, 1);
    },
    duplicateCheck(item) {
      this.duplicateCheckParams.hasError = false;
      this.duplicateCheckParams.isWaiting = true;
      this.duplicateCheckParams.keyword = item.keyword;

      const params = {
        keyword: item.keyword
      };
      apiProjects.relatedWordAnalysis
        .duplicateCheck(
          params,
          this.auth.idToken,
          this.project.id,
          this.keywordId
        )
        .then(response => {
          this.insertPagesCount(response.data.items);
          this.duplicateCheckParams.isWaiting = false;
        })
        .catch(error => {
          this.duplicateCheckParams.hasError = true;
          this.duplicateCheckParams.isWaiting = false;
          this.duplicateCheckParams.keyword = '';
        });
    },
    // itemsに重複チェックのレスポンス内容をねじり込む
    insertPagesCount(result) {
      let items = this.data.items.slice();
      this.data.items = items.map(item => {
        const index = result.findIndex(
          resultItem => resultItem.keyword === item.keyword
        );
        if (index != -1) {
          item.duplicatePagesCount = result[index].count;
        } else {
          // 該当するキーがない場合は-1を挿入
          item.duplicatePagesCount = -1;
        }
        return item;
      });
    }
  }
};
</script>
